/**
 * Booking Status Constants
 */
export const BOOKING_STATUS = {
  PENDING: 'pending',
  ASSIGNED: 'assigned',
  IN_TRANSIT: 'in_transit',
  PATIENT_PICKED: 'patient_picked',
  PATIENT_DROPPED: 'patient_dropped',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type BookingStatus = typeof BOOKING_STATUS[keyof typeof BOOKING_STATUS];

/**
 * Booking Type Constants
 */
export const BOOKING_TYPE = {
  TO_HOSPITAL: 'to_hospital', // Pick from location -> Drop at hospital
  FROM_HOSPITAL: 'from_hospital', // Pick from hospital -> Drop at location
} as const;

export type BookingType = typeof BOOKING_TYPE[keyof typeof BOOKING_TYPE];

/**
 * Booking Type Labels
 */
export const BOOKING_TYPE_LABELS: Record<BookingType, string> = {
  [BOOKING_TYPE.TO_HOSPITAL]: 'To Hospital',
  [BOOKING_TYPE.FROM_HOSPITAL]: 'From Hospital',
} as const;

/**
 * Booking Type Colors
 * Distinct colors that are not used in priority or status
 */
export const BOOKING_TYPE_COLORS: Record<BookingType, string> = {
  [BOOKING_TYPE.TO_HOSPITAL]: '#6366f1', // Indigo - distinct from status/priority colors
  [BOOKING_TYPE.FROM_HOSPITAL]: '#ec4899', // Pink - distinct from status/priority colors
} as const;

/**
 * Color Constants
 * Centralized color definitions for consistent theming
 */
export const COLORS = {
  // Status Colors
  STATUS: {
    PENDING: '#94a3b8',
    ASSIGNED: '#3b82f6',
    IN_TRANSIT: '#ef4444',
    PATIENT_PICKED: '#10b981',
    PATIENT_DROPPED: '#795548',
    COMPLETED: '#10b981',
    CANCELLED: '#fa003f',
    WARNING: '#f59e0b',
  },
  // Theme Colors (Dark Mode)
  THEME: {
    DARK: {
      BACKGROUND: '#1e293b',
      BORDER: '#334155',
      TEXT_PRIMARY: '#f1f5f9',
      TEXT_SECONDARY: '#94a3b8',
      TEXT_TERTIARY: '#64748b',
      TEXT_MUTED: '#cbd5e1',
    },
    // Theme Colors (Light Mode)
    LIGHT: {
      BACKGROUND: '#ffffff',
      BORDER: '#e2e8f0',
      TEXT_PRIMARY: '#0f172a',
      TEXT_SECONDARY: '#64748b',
      TEXT_TERTIARY: '#94a3b8',
      TEXT_MUTED: '#475569',
    },
  },
} as const;

/**
 * Booking Status Labels
 */
// Legacy 'arrived' supported for backward compatibility (displayed as Patient Picked Up)
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> & { arrived?: string } = {
  [BOOKING_STATUS.PENDING]: 'Waiting For Assignment',
  [BOOKING_STATUS.ASSIGNED]: 'Assigned',
  [BOOKING_STATUS.IN_TRANSIT]: 'On Way To Pick Patient',
  [BOOKING_STATUS.PATIENT_PICKED]: 'Patient Picked Up',
  [BOOKING_STATUS.PATIENT_DROPPED]: 'Patient Dropped',
  [BOOKING_STATUS.COMPLETED]: 'Completed',
  [BOOKING_STATUS.CANCELLED]: 'Cancelled',
  arrived: 'Patient Picked Up', // legacy
} as const;

/**
 * Booking Status Colors
 */
export const BOOKING_STATUS_COLORS: Record<BookingStatus, string> & { arrived?: string } = {
  [BOOKING_STATUS.PENDING]: COLORS.STATUS.PENDING,
  [BOOKING_STATUS.ASSIGNED]: COLORS.STATUS.ASSIGNED,
  [BOOKING_STATUS.IN_TRANSIT]: COLORS.STATUS.IN_TRANSIT,
  [BOOKING_STATUS.PATIENT_PICKED]: COLORS.STATUS.PATIENT_PICKED,
  [BOOKING_STATUS.PATIENT_DROPPED]: COLORS.STATUS.PATIENT_DROPPED,
  [BOOKING_STATUS.COMPLETED]: COLORS.STATUS.COMPLETED,
  [BOOKING_STATUS.CANCELLED]: COLORS.STATUS.CANCELLED,
  arrived: COLORS.STATUS.PATIENT_PICKED, // legacy
} as const;

/**
 * Booking Status Display Labels (Short)
 */
export const BOOKING_STATUS_DISPLAY: Record<BookingStatus, string> & { arrived?: string } = {
  [BOOKING_STATUS.PENDING]: 'Waiting',
  [BOOKING_STATUS.ASSIGNED]: 'Assigned',
  [BOOKING_STATUS.IN_TRANSIT]: 'On way to pick',
  [BOOKING_STATUS.PATIENT_PICKED]: 'Patient picked up',
  [BOOKING_STATUS.PATIENT_DROPPED]: 'Patient dropped',
  [BOOKING_STATUS.COMPLETED]: 'Completed',
  [BOOKING_STATUS.CANCELLED]: 'Cancelled',
  arrived: 'Patient picked up', // legacy
} as const;

/**
 * Active Booking Statuses (for filtering)
 */
export const ACTIVE_BOOKING_STATUSES: BookingStatus[] = [
  BOOKING_STATUS.ASSIGNED,
  BOOKING_STATUS.IN_TRANSIT,
  BOOKING_STATUS.PATIENT_PICKED,
  BOOKING_STATUS.PATIENT_DROPPED,
];

/**
 * Route View Modes
 */
export const ROUTE_VIEW_MODE = {
  CURRENT: 'current',
  DAY: 'day',
} as const;

export type RouteViewMode = typeof ROUTE_VIEW_MODE[keyof typeof ROUTE_VIEW_MODE];

/**
 * Tracking Tabs
 */
export const TRACKING_TAB = {
  LIVE: 'live',
  STAFF: 'staff',
  BOOKINGS: 'bookings',
} as const;

export type TrackingTab = typeof TRACKING_TAB[keyof typeof TRACKING_TAB];

